import { Injectable } from '@angular/core';
import { isDate } from 'lodash';
import { determine } from 'jstimezonedetect';
import * as momentNs from 'moment';
import { Moment } from 'moment';
import 'moment-duration-format'; // patches the global moment

const moment = momentNs; // Workaround to fix issues with rollout packager
import Base = momentNs.unitOfTime.Base;
import Diff = momentNs.unitOfTime.Diff;
import StartOf = momentNs.unitOfTime.StartOf;

export const MOMENT_TIMEZONELESS_DATE_FORMAT = 'YYYY-MM-DD';
export const MOMENT_LONG_ABBREVIATED_DATE_FORMAT = 'll';
export const MOMENT_LONG_WEEKDAY_DATE_TIME_FORMAT = 'LLLL';
export const MOMENT_MONTH_YEAR_DATE_FORMAT = 'MMMM YYYY';
export const MOMENT_SHORT_MONTH_YEAR_DATE_FORMAT = 'MMM YYYY';
export const MOMENT_YEAR_DATE_FORMAT = 'YYYY';
export const MOMENT_MONTH_DATE_FORMAT = 'MMMM';
export const MOMENT_SHORT_WEEK_DATE_FORMAT = 'ddd';
export const MOMENT_LONG_DATE_FORMAT = 'LL';
export const MOMENT_AM_PM_TIME_FORMAT = 'LT';
export const MOMENT_FULL_AM_PM_TIME_FORMAT = 'lll';
export const MOMENT_LONG_TIME_FORMAT = 'HH:mm:ss';

export const HOUR_AS_SECONDS = 3600;
export const DAY_AS_SECONDS = 86400;
export const TIME_15_MINUTES_AS_SECONDS = 900;

export interface IParsedDate {
  day: number;
  month: number;
  week: number;
  year: number;
}

export interface IParsedDuration {
  hours: number;
  minutes: number;
  seconds: number;
}

type MomentPeriod = 'day' | 'week' | 'month' | 'year';

@Injectable({
  providedIn: 'root',
})
export class DateTimeService {
  constructor() {}

  getTimezone(): string {
    return determine().name();
  }

  calculateNumberOfUnitsFromDate(
    currentTime: string,
    baseTime: string,
    startOf: Base = 'day',
    timeUnit: Base = 'days',
    partialUnits: boolean = false
  ): number {
    return moment(currentTime, MOMENT_TIMEZONELESS_DATE_FORMAT).diff(
      moment(baseTime, MOMENT_TIMEZONELESS_DATE_FORMAT).startOf(startOf),
      timeUnit,
      partialUnits
    );
  }

  setDateLocale(locale: string) {
    moment.locale(locale);
  }

  getMilliseconds(
    date: string,
    format: string = MOMENT_TIMEZONELESS_DATE_FORMAT
  ): number {
    const dateString = !!format ? moment(date, format) : moment(date);

    return dateString.valueOf();
  }

  getDateString(
    value: string | Date = this.getDate(new Date()),
    format?: string
  ): string {
    const date = this.getDate(value || moment().format());

    return this.formatDate(moment(date).hour(12), format);
  }

  getDate(value: string | Date): Date {
    let utcTimestamp;
    if (isDate(value)) {
      utcTimestamp = Date.parse(this.formatDate(value, null));

      return new Date(utcTimestamp);
    } else {
      utcTimestamp = Date.parse(value);

      if (isNaN(utcTimestamp)) {
        throw new Error(`Cannot parse '${value}' as a date`);
      }

      /*
       Using new Date(utcTimestamp) would cause the created date to be adjusted for
       the local timezone. We manually push the timestamp back here so that when we
       call new Date(timestamp) it will return correctly.
       */
      const timezoneOffsetInMinutes = new Date(
        utcTimestamp
      ).getTimezoneOffset(); // calculate timezone offset of
      // desired date instead of current
      // date in case the current date and
      // the desired date have different
      // timezone offsets (e.g. daylight
      // savings)
      const offsetInMilliseconds = timezoneOffsetInMinutes * 60000;
      const timestamp = utcTimestamp + offsetInMilliseconds;

      return new Date(timestamp);
    }
  }

  addTime(
    baseTime: string,
    timeAmount: number,
    timeUnit: Diff,
    format: string = MOMENT_TIMEZONELESS_DATE_FORMAT,
    utc: boolean = false,
    midDay: boolean = true
  ) {
    const parsedDate = moment(baseTime, format).add(timeAmount, timeUnit);

    return this.formatDate(
      midDay ? parsedDate.hour(12) : parsedDate,
      format,
      utc
    );
  }

  getNow(format?: string, utc: boolean = false): string {
    return this.formatDate(moment(), format, utc);
  }

  getDateFromWeek(
    year: number,
    week: number,
    format: string = MOMENT_TIMEZONELESS_DATE_FORMAT,
    utc: boolean = false
  ): string {
    const date = moment().year(year).week(week);

    return this.formatDate(date, format, utc);
  }

  getCurrent(period: MomentPeriod): number {
    return moment()[period]();
  }

  formatDate(
    date: string | Date | Moment = this.getDate(new Date()),
    format: string = MOMENT_TIMEZONELESS_DATE_FORMAT,
    utc: boolean = false
  ): string {
    return (utc ? moment.utc(date) : moment(date)).format(format);
  }

  isValidDate(
    date: string | Date | Moment = this.getDate(new Date()),
    format: string = MOMENT_TIMEZONELESS_DATE_FORMAT,
    utc: boolean = false
  ): boolean {
    return (utc
      ? moment.utc(date, format, true)
      : moment(date, format, true)
    ).isValid();
  }

  getMidDayUtcOfDate(
    date: string | Date | Moment = this.getDate(new Date()),
    utc: boolean = false
  ): string {
    return this.formatDate(
      utc ? moment.utc(date).hour(12) : moment(date).hour(12),
      null,
      true
    );
  }

  getStartOf(
    date: string | Date | Moment = this.getDate(new Date()),
    period: StartOf = 'day',
    format: string = MOMENT_TIMEZONELESS_DATE_FORMAT,
    utc: boolean = false
  ): string {
    return this.formatDate(
      utc ? moment.utc(date).startOf(period) : moment(date).startOf(period),
      format,
      utc
    );
  }

  getEndOf(
    date: string | Date | Moment = this.getDate(new Date()),
    period: StartOf = 'day',
    format: string = MOMENT_TIMEZONELESS_DATE_FORMAT,
    utc: boolean = false
  ): string {
    return this.formatDate(
      utc ? moment.utc(date).endOf(period) : moment(date).endOf(period),
      format,
      utc
    );
  }

  convertToUtc(
    date: string | Date | Moment = this.getDate(new Date()),
    format: string = MOMENT_TIMEZONELESS_DATE_FORMAT
  ): string {
    return moment(date).utc().format(format);
  }

  convertToLocal(
    date: string | Date | Moment = this.getDate(new Date()),
    format: string = MOMENT_TIMEZONELESS_DATE_FORMAT
  ): string {
    return moment(date).local().format(format);
  }

  isBefore(time: string, baseTime: string): boolean {
    return moment(time).isBefore(baseTime);
  }

  isAfter(time: string, baseTime: string): boolean {
    return moment(time).isAfter(baseTime);
  }

  isSameDate(time: string, baseTime: string): boolean {
    const localTime = moment(time, MOMENT_TIMEZONELESS_DATE_FORMAT);
    const isSameFn = localTime.isSame.bind(
      localTime,
      moment(baseTime, MOMENT_TIMEZONELESS_DATE_FORMAT)
    );

    return isSameFn('day') && isSameFn('month') && isSameFn('year');
  }

  getFirstDayOfWeek(): number {
    return moment().startOf('week').isoWeekday() % 7;
  }

  parse(
    date: string,
    format: string = MOMENT_TIMEZONELESS_DATE_FORMAT
  ): IParsedDate {
    const parsedDate = moment(date, format);

    return {
      day: parsedDate.date(),
      month: parsedDate.month(),
      week: parsedDate.week(),
      year: parsedDate.year(),
    };
  }

  formatDuration(
    seconds: number,
    format: string = MOMENT_LONG_TIME_FORMAT
  ): string {
    // NOTE: Add `any` to solve typing issue in moment-duration-format plugin
    // @see https://github.com/DefinitelyTyped/DefinitelyTyped/pull/14327
    return (moment.duration(seconds, 'seconds') as any).format(format, {
      trim: false,
    });
  }

  parseDuration(seconds: number): IParsedDuration {
    const parsedDuration = moment.duration(seconds, 'seconds');

    return {
      hours: parsedDuration.hours(),
      minutes: parsedDuration.minutes(),
      seconds: parsedDuration.seconds(),
    };
  }

  calculateNumberOfUnitsFromTime(
    baseTime: string,
    currentTime: string,
    timeUnit: Diff = 'seconds',
    utc: boolean = false
  ): number {
    const timeA = utc ? moment.utc(baseTime) : moment(baseTime);
    const timeB = utc ? moment.utc(currentTime) : moment(currentTime);

    return timeB.diff(timeA, timeUnit);
  }

  fromStartOfTheDay(
    date: string,
    timeUnit: Diff = 'seconds',
    utc: boolean = false
  ): number {
    const currentTime = utc ? moment.utc(date) : moment(date);

    return currentTime.diff(currentTime.clone().startOf('day'), timeUnit);
  }

  roundTime(
    date: string,
    duration: number,
    format: string,
    utc: boolean = false,
    timeUnit: Diff = 'minutes',
    method: 'ceil' | 'floor' = 'ceil'
  ): string {
    const parsedDate = utc ? moment.utc(date) : moment(date);
    const parsedDuration = moment.duration(duration, timeUnit);
    const roundedDate =
      Math[method](+parsedDate / +parsedDuration) * +parsedDuration;

    return (utc ? moment.utc(roundedDate) : moment(roundedDate)).format(format);
  }

  getTimeRange(
    minDateTime: string,
    maxDateTime: string,
    increment: number,
    timeUnit: Diff = 'seconds',
    utc: boolean = false
  ): string[] {
    const start = utc ? moment.utc(minDateTime) : moment(minDateTime);
    const end = utc ? moment.utc(maxDateTime) : moment(maxDateTime);
    const maxEnd = start.clone().add(1, 'day');
    const validEnd = end.isBefore(maxEnd) ? end : maxEnd;
    const range = [];

    let time = start.clone();

    while (time.valueOf() <= validEnd.valueOf()) {
      range.push(time.format());

      time = time.add(increment, timeUnit);
    }

    return range;
  }

  mergeDateAndTime(
    date: string,
    time: string,
    format: string,
    timeUnit: Diff = 'seconds'
  ): string {
    const baseDate = moment(date);
    const duration = this.fromStartOfTheDay(time, timeUnit);

    baseDate.startOf('day');
    baseDate.add(duration, timeUnit);

    return baseDate.format(format);
  }
}
